import store from "@/store";
import logger from "@/services/Logger";
import Helpers from "@/services/helpers";
import { SET_RUNNING, SET_PAUSED, SET_PRELOAD } from "@/store/mutation-types";

export default {
  name: "playback",
  install(ctx) {
    Helpers.mapStore(ctx, {
      running: {
        get: "player/running",
        set: `player/${SET_RUNNING}`
      },
      preload: {
        get: "player/preload",
        set: `player/${SET_PRELOAD}`
      },
      paused: {
        get: "player/paused",
        set: `player/${SET_PAUSED}`
      }
    });

    let rewindTimer = null;
    let preloadTrack = null;

    const watchLoadingProgress = function(track) {
      const { node } = track;
      if (node) {
        this.preload = 0;

        node.progressHandler = () => {
          setTimeout(() => {
            const { duration, buffered, currentTime } = node;

            if (duration > 0) {
              if (currentTime > duration / 2 && !preloadTrack) {
                preloadNext();
              }

              for (let i = 0; i < buffered.length; i++) {
                const range = buffered.length - 1 - i;

                if (buffered.start(range) < currentTime) {
                  this.preload = (buffered.end(range) / duration) * 100;
                  break;
                }
              }
            }
          });
        };

        node.addEventListener("timeupdate", node.progressHandler);
      }
    }.bind(ctx);

    const stopWatchLoadingProgress = function(track) {
      const { node } = track;

      if (node) {
        node.removeEventListener("timeupdate", node.progressHandler);
      }
    }.bind(ctx);

    const isNotPlaylist = function(tracklist = {}) {
      return this.playlist ? tracklist.id !== this.playlist.id : true;
    }.bind(ctx);

    const add = function(tracks) {
      const playlistTracks = this.playlist
        ? [...this.playlist.tracks, ...tracks]
        : tracks;
      const id = Helpers.getTracklistId(playlistTracks);

      this.mod.queue.add({ tracks });
      this.playlist = { id, tracks: playlistTracks };
    }.bind(ctx);

    const remove = function(trackId) {
      const playlistTracks = this.playlist.tracks.filter(
        _track => _track !== trackId
      );

      if (playlistTracks.length) {
        this.mod.queue.remove(trackId);

        setTimeout(() => {
          this.playlist = {
            id: Helpers.getTracklistId(playlistTracks),
            tracks: playlistTracks
          };

          if (this.currentTrack && this.currentTrack.id === trackId) {
            // save prev state
            const paused = this.paused;

            this.next();

            if (paused) {
              this.stop();
            }
          }
        });
      } else {
        this.clearQueue();
      }
    }.bind(ctx);

    const stop = function() {
      if (this.currentTrack) {
        logger.log("PLAYER", `STOPED, TRACK: ${this.currentTrack.name}`);

        stopWatchLoadingProgress(this.currentTrack);
        rewind();

        this.currentTrack.stop();
        this.currentTrack = null;

        preloadTrack = null;
      }

      this.mod.core.seek = 0;
      this.running = false;
      this.paused = false;
      this.preload = 0;
      this.progress = 0;

      store.dispatch("page/setTitle");

      this.mod.session.clear();
    }.bind(ctx);

    const preloadNext = async function() {
      const nextIndex = this.mod.core.getNextIndex();
      const nextTrackId = this.mod.queue.tracklistIds[nextIndex];
      const nextTrack = this.mod.queue.tracksStore[nextTrackId];

      if (nextTrack) {
        preloadTrack = this.mod.track.create(nextTrack);
      }
    }.bind(ctx);

    const play = async function({ index = 0, tracklist = null }) {
      const isNotOwner = () => !(tracklist && tracklist.owner);

      if (this.currentIndex !== index && isNotOwner()) {
        stop();
      }

      if (tracklist && isNotPlaylist(tracklist)) {
        this.clearQueue();
        this.mod.queue.setIndex(0);

        await this.mod.queue.add(tracklist);

        this.playlist = {
          id: tracklist.id,
          tracks: tracklist.tracks
        };

        this.mod.shuffle.fill();
      }

      if (this.tracklistIds.length) {
        if (!this.paused && isNotOwner()) {
          this.mod.queue.setIndex(index);
        }

        if (!this.currentTrack) {
          this.currentTrack = this.mod.track.create(
            this.mod.queue.currentTrack
          );
          watchLoadingProgress(this.currentTrack);
        } else {
          this.currentTrack.play();
        }

        this.running = true;
        this.paused = false;
      }

      store.dispatch("page/setTitle");

      this.mod.session.set();
    }.bind(ctx);

    const pause = function() {
      if (this.currentTrack) {
        this.currentTrack.pause();
      }

      this.running = false;
      this.paused = true;

      store.dispatch("page/setTitle");
    }.bind(ctx);

    const toggle = function() {
      this.running ? pause() : play({ index: this.currentIndex });
    }.bind(ctx);

    const prev = function() {
      play({ index: this.mod.core.getPrevIndex() });
    }.bind(ctx);

    const next = function(force) {
      const nextIndex = this.mod.core.getNextIndex(force);

      if (force && this.repeat === "track") {
        this.toggleRepeat("playlist");
      }

      if (nextIndex !== null) {
        play({ index: nextIndex });
      } else {
        stop();
      }
    }.bind(ctx);

    const rewind = function(per = 0, lazy = false) {
      if (this.currentTrack) {
        const trackSeek = this.currentTrack.duration * per;
        const diff = trackSeek - this.mod.core.seek;

        if (lazy) {
          this.mod.core.setProgress(trackSeek);
        } else {
          clearTimeout(rewindTimer);
          rewindTimer = setTimeout(() => {
            this.mod.core.seekTo(diff);
          }, 50);
        }
      }
    }.bind(ctx);

    return {
      add,
      remove,
      stop,
      play,
      pause,
      toggle,
      prev,
      next,
      rewind
    };
  }
};
